import axios from "axios";
import type {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { store } from "../store";

// Base API URL - adjust this to match your backend
// const API_BASE_URL = "http://localhost:8000/api/v1";
const API_BASE_URL = "https://api.djichami.org/api/v1";

// Create public request instance (unauthenticated)
export const publicRequest: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Enable cookies for CORS requests
});

// Add language header interceptor for public requests
publicRequest.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const state = store.getState();
    const language = state.language.currentLanguage;
    config.headers["Accept-Language"] = language;
    return config;
  },
  (error) => Promise.reject(error)
);

// Create private request instance (authenticated)
export const privateRequest: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Enable cookies for CORS requests
});

// Request interceptor for private requests - add auth token
privateRequest.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const state = store.getState();
    const token = state.auth.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add language header
    const language = state.language.currentLanguage;
    config.headers["Accept-Language"] = language;

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for private requests - handle token refresh
privateRequest.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip if no response (network error) or if request was already retried
    if (!error.response || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Check if error is due to expired/invalid token (401 Unauthorized)
    const isUnauthorized = error.response?.status === 401;

    // Check for various token expiration indicators
    const errorMessage = error.response?.data?.message || "";
    const debugMessage = error.response?.data?.debug?.message || "";
    const exceptionType = error.response?.data?.debug?.exception || "";

    const isTokenExpired =
      isUnauthorized &&
      (errorMessage.toLowerCase().includes("expired") ||
        errorMessage.toLowerCase().includes("token") ||
        debugMessage.toLowerCase().includes("expired") ||
        debugMessage === "Token has expired" ||
        exceptionType.includes("TokenExpiredException") ||
        exceptionType.includes("UnauthorizedHttpException"));

    if (isTokenExpired) {
      // Mark request as retried to prevent infinite loops
      originalRequest._retry = true;

      const state = store.getState();
      const accessToken = state.auth.accessToken;
      const lang = state.language.currentLanguage;

      // If no access token, redirect to login
      if (!accessToken) {
        store.dispatch({ type: "auth/logout" });
        window.location.href = `/${lang}/auth/login`;
        return Promise.reject(error);
      }

      try {
        // Attempt to refresh token
        // The refresh endpoint uses jwt.refresh middleware which allows expired tokens
        // Send the expired token in Authorization header
        // The refresh token cookie is automatically sent via withCredentials: true
        const refreshResponse = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {}, // Empty body - token comes from Authorization header
          {
            headers: {
              Authorization: `Bearer ${accessToken}`, // Send expired token
              "Content-Type": "application/json",
              "Accept-Language": lang,
            },
            withCredentials: true, // Include cookies (refresh token)
          }
        );

        // Extract new access token from response
        // Response structure: { success: true, data: { token: "...", ... }, message: "..." }
        const newAccessToken = refreshResponse.data.data?.token;

        if (!newAccessToken) {
          console.error(
            "Token refresh failed: No token in response",
            refreshResponse.data
          );
          throw new Error("No token in refresh response");
        }

        // Update store with new access token
        // Refresh token is automatically stored in cookie by backend
        store.dispatch({
          type: "auth/refreshToken",
          payload: {
            accessToken: newAccessToken,
            // Don't update refreshToken in Redux - it's in cookie now
          },
        });

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return privateRequest(originalRequest);
      } catch (refreshError: any) {
        // Refresh failed, log error and redirect to login
        console.error("Token refresh failed:", refreshError);

        // Clear auth state
        store.dispatch({ type: "auth/logout" });

        // Redirect to login page
        const currentLang = store.getState().language.currentLanguage || lang;
        window.location.href = `/${currentLang}/auth/login`;

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default { publicRequest, privateRequest };
